import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

/** A small history router: paths map to screens, and "back" behaves like the prototype's stack. */
export interface Route {
  path: string;
  params: Record<string, string>;
  search: URLSearchParams;
}

const PATTERNS: [RegExp, string][] = [
  [/^\/$/, 'home'], [/^\/menu$/, 'menu'], [/^\/build$/, 'build'], [/^\/bag$/, 'bag'], [/^\/order\/([^/]+)$/, 'status'], [/^\/card$/, 'home'], [/^\/card\/back$/, 'cardback'], [/^\/card\/activity$/, 'activity'], [/^\/code$/, 'home'], [/^\/offers$/, 'home'], [/^\/rewards$/, 'home'],
  [/^\/account$/, 'account'], [/^\/account\/([^/]+)$/, 'accountpage'], [/^\/messages$/, 'messages'], [/^\/invite$/, 'invite'], [/^\/creator$/, 'creator'], [/^\/join$/, 'join'], [/^\/pay\/return$/, 'payreturn'], [/^\/catering$/, 'cater'], [/^\/catering\/orders$/, 'corders'], [/^\/catering\/help$/, 'chelp'], [/^\/catering\/order\/([^/]+)$/, 'corder'], [/^\/t\/([^/]+)$/, 'teampick'], [/^\/group$/, 'group'], [/^\/g\/([^/]+)$/, 'grouplink'], [/^\/legal\/?$/, 'legal'], [/^\/legal\/([^/]+)$/, 'legaldoc'],
];

export function screenFor(path: string): { screen: string; params: Record<string, string> } {
  for (const [re, screen] of PATTERNS) {
    const m = re.exec(path);
    if (m) return { screen, params: m[1] ? { id: m[1] } : {} };
  }
  return { screen: 'notfound', params: {} };
}

interface Nav {
  route: Route;
  screen: string;
  navigate: (path: string, opts?: { replace?: boolean; root?: boolean }) => void;
  back: () => void;
  depth: number;
}
const NavContext = createContext<Nav | null>(null);

function current(): Route {
  return { path: location.pathname, params: screenFor(location.pathname).params, search: new URLSearchParams(location.search) };
}

export function RouterProvider({ children }: { children: ReactNode }) {
  const [route, setRoute] = useState<Route>(current);
  const [depth, setDepth] = useState<number>(() => (history.state?.depth as number | undefined) ?? 0);
  useEffect(() => {
    if (!history.state || typeof history.state.depth !== 'number') history.replaceState({ depth: 0 }, '', location.href);
    const onPop = () => {
      setRoute(current());
      setDepth((history.state?.depth as number | undefined) ?? 0);
      window.scrollTo(0, 0);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  const navigate: Nav['navigate'] = (path, opts = {}) => {
    if (path === location.pathname + location.search && !opts.replace) return;
    const d = opts.root ? 0 : opts.replace ? depth : depth + 1;
    if (opts.replace) history.replaceState({ depth: d }, '', path);
    else history.pushState({ depth: d }, '', path);
    setDepth(d);
    setRoute(current());
  };
  const back = () => {
    if (depth > 0 && history.length > 1) history.back();
    else navigate('/', { root: true, replace: true });
  };
  const { screen } = screenFor(route.path);
  return <NavContext.Provider value={{ route, screen, navigate, back, depth }}>{children}</NavContext.Provider>;
}

export function useNav(): Nav {
  const n = useContext(NavContext);
  if (!n) throw new Error('RouterProvider missing');
  return n;
}
